import { ChangeEvent, useCallback, useEffect, useState } from 'react';

import Cropper, { type Area, type Point } from 'react-easy-crop';

import { Modal } from '../../modal/Modal';
import { Button } from '../../buttons/Button';

import type { TAvatarCropTexts } from '../../../types';

import { createCroppedImageBlob } from './crop-image';
import styles from './avatar-crop-dialog.module.scss';

const INITIAL_CROP: Point = { x: 0, y: 0 };
const INITIAL_ZOOM = 1;
const MAX_ZOOM = 3;

interface AvatarCropDialogProps {
  fileName: string;
  fileType: string;
  imageSrc: string | null;
  isOpen: boolean;
  texts: TAvatarCropTexts;
  onClose: () => void;
  onConfirm: (file: File) => void;
}

function createCroppedFile(
  blob: Blob,
  fileName: string,
  fileType: string,
): File {
  const mimeType = blob.type || fileType || 'image/png';

  return new File([blob], fileName, { type: mimeType });
}

export function AvatarCropDialog({
  fileName,
  fileType,
  imageSrc,
  isOpen,
  texts,
  onClose,
  onConfirm,
}: AvatarCropDialogProps) {
  const [crop, setCrop] = useState<Point>(INITIAL_CROP);
  const [zoom, setZoom] = useState(INITIAL_ZOOM);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setCrop(INITIAL_CROP);
      setZoom(INITIAL_ZOOM);
      setCroppedAreaPixels(null);
      setIsProcessing(false);
      return;
    }

    setCrop(INITIAL_CROP);
    setZoom(INITIAL_ZOOM);
    setCroppedAreaPixels(null);
  }, [isOpen]);

  const handleCropComplete = useCallback(
    (_croppedArea: Area, currentCroppedAreaPixels: Area) => {
      setCroppedAreaPixels(currentCroppedAreaPixels);
    },
    [],
  );

  const handleZoomChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setZoom(Number(event.target.value));
    },
    [],
  );

  const handleConfirm = useCallback(async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    setIsProcessing(true);

    try {
      const croppedBlob = await createCroppedImageBlob(
        imageSrc,
        croppedAreaPixels,
        fileType,
      );

      if (croppedBlob) {
        onConfirm(createCroppedFile(croppedBlob, fileName, fileType));
      }
    } finally {
      setIsProcessing(false);
    }
  }, [croppedAreaPixels, fileName, fileType, imageSrc, onConfirm]);

  return (
    <Modal
      classNameModal={styles.modal}
      description={texts.modalDescription}
      isOpen={isOpen}
      onBeforeClose={onClose}
      preventOutsideClose={isProcessing}
      title={texts.modalTitle}
      size="m"
    >
      <div className={styles.dialogContent} data-testid="avatar-crop-dialog">
        <div className={styles.copy}>
          <h2 className={styles.title}>{texts.title}</h2>
          <p className={styles.description}>{texts.description}</p>
        </div>

        <div className={styles.cropperSurface}>
          {imageSrc && (
            <Cropper
              aspect={1}
              crop={crop}
              cropShape="round"
              image={imageSrc}
              maxZoom={MAX_ZOOM}
              minZoom={INITIAL_ZOOM}
              onCropChange={setCrop}
              onCropComplete={handleCropComplete}
              onZoomChange={setZoom}
              showGrid={false}
              zoom={zoom}
            />
          )}
        </div>

        <div className={styles.zoomSection}>
          <div className={styles.zoomHeader}>
            <label className={styles.zoomLabel} htmlFor="avatar-crop-zoom">
              {texts.zoom}
            </label>
            <span className={styles.zoomValue}>{Math.round(zoom * 100)}%</span>
          </div>

          <input
            className={styles.zoomInput}
            data-testid="avatar-crop-zoom"
            id="avatar-crop-zoom"
            max={MAX_ZOOM}
            min={INITIAL_ZOOM}
            onChange={handleZoomChange}
            step={0.1}
            type="range"
            value={zoom}
          />
        </div>

        <div className={styles.actions}>
          <Button
            className={styles.actionButton}
            color="contrast"
            data-testid="avatar-crop-cancel"
            onClick={onClose}
            size="s"
            type="button"
            variant="outline"
          >
            {texts.cancel}
          </Button>
          <Button
            className={styles.actionButton}
            color="accent"
            data-testid="avatar-crop-confirm"
            disabled={!croppedAreaPixels || isProcessing}
            onClick={handleConfirm}
            size="s"
            type="button"
            variant="outline"
          >
            {isProcessing ? texts.processing : texts.confirm}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
